import React, {useEffect, useState} from 'react';
import PropTypes from "prop-types";
import css from './Calendar.css';
import moment from 'moment';
import useScrollWidth from '../hooks/useScrollWidth';
import { sortBy, flatten } from "lodash";

const pairs = arr => flatten(
  arr
    .map((v, i) => arr.slice(i + 1)
    .map(w => [v, w]))
);

const Calendar = ({
  startHour,
  endHour,
  rowSize,
  data,
  verticalPadding,
  fontSize,
  onEventClick,
  onHeaderClick,
  selectedDate,
  focusID
}) => {
  const scrollWidth = useScrollWidth();
  const [focusElement, setFocusElement] = useState(null);

  // Return the class with selected styling if given index is current day of week
  // or the selected date
  const getSelectedClass = (index, className) => {
    const isInWeek = moment().isSame(moment(selectedDate), "week");
    const isCurrentDay = isInWeek && moment().day() === index;
    const isSelectedDay = moment(selectedDate).day() === index ;

    if ( isCurrentDay ) return [className].concat(css['column-today']).join(" ");
    if ( isSelectedDay ) return [className].concat(css['column-selected']).join(" ");
    return className;
  };

  const numberOfHours = endHour - startHour + 1;

  const getBackgroundColor = type => {
    switch (type) {
      case "ASSIGNMENT":
        return "#D20061";
      case "APPOINTMENT":
      case "GROUP_SESSION":
        return "#2381C0";
      case "COURSE":
        return "#37A2A2";
      case "TO_DO":
      case "FLAG":
      case "REFERRAL":
        return "#B94713";
      case "CLASS":
        return "#37A2A2";
      case "MULTIPLE":
        return "#4A4A4A";
    }
  } 

  const getAllDayBackgroundColor = events => 
    getBackgroundColor(events.length > 1 ? "MULTIPLE" : events[0].type);

  const getLines = () => {    
    return <div className={css['line-container']} aria-hidden={true}>
      {new Array(numberOfHours).fill(0).map((_, i) => (
        <div 
          key={i}
          className={css['line']} 
          style={{height: rowSize}} 
        />
      ))}
    </div>
  };

  // For screenreaders, this will determine the correct element to focus on
  // after a to-do is closed
  useEffect(() => {
    if (focusElement) {
      if (document.querySelector(focusElement.selector) !== null) {
        document.querySelector(focusElement.selector).focus();
      }
      else document.querySelector(focusElement.fallBackSelector).focus();
    }
  }, [focusID]);

  // Get the duration, in minutes, for two strings in the format HH:mm. If the diff is
  // negative, we assume the event finishes in the next day and therefore sum the
  // number of minutes in a day 24 * 60 = 1440
  const getDuration = (startTime, endTime) => {
    const diff = moment(endTime, "HH:mm").diff(moment(startTime, "HH:mm"), 'minutes');
    return diff < 0 ? diff + 1440 : diff;
  }

  const getLabel = (date, startTime, endTime, name) => {
    const day = moment(date).format("dddd, MMMM DD");
    let time = "";
    if ( endTime ) {
      time = `${moment(startTime, "HH:mm").format("h:mma")} to ${moment(endTime, "HH:mm").format("h:mma")}`;
    } else time = "All day";
    return `${day}, ${time}, ${name}`;
  }

  const getEventItem = event => {
    const { date, startTime, endTime, id, type, name } = event;
    const [hour, start] = startTime.split(":").map(value => parseInt(value))
    const duration = getDuration(startTime, endTime);

    // Add a small offset so blocks can be contained between horizontal lines
    // and still be completely visible
    const blockSpaceOffset = 2;

    // Check how far from the top the block should be rendered.
    // E.g. the first available time slot will have a 0 offset
    const timeSlotOffset = rowSize * (hour + numberOfHours - 24);

    // Get block height by getting the proportion of the event length and discounting
    // the block space offset so we can visualize the bottom horizontal line
    const height = (rowSize * duration / 60) - blockSpaceOffset;

    // Now, we need to discount the offset inside the time block. 
    // E.g. a meeting that starts in the minute 30 should be visible from the middle
    // of the block
    const insideOffset = rowSize * start / 60;

    // Get the final top position
    const top = timeSlotOffset + insideOffset + blockSpaceOffset;

    // Now we need to get the visible height, once events shouldn't overflow. If an 
    // element is over the max height, just cut the height and fit it inside the remaining
    // space
    const maxHeight = rowSize * numberOfHours;
    const visibleHeight = top + height <= maxHeight 
      ? height 
      : height - (top + height - maxHeight);

    const eventName = event.numberOfEvents > 1 ? `${event.numberOfEvents} events` : event.name;
    return (
      <button
        key={id}
        aria-label={getLabel(date, startTime, endTime, eventName)}
        className={css['event']}
        onClick={() => {
          setFocusElement(null);
          onEventClick(
            data.filter(e => e.date === date && e.startTime >= startTime && e.endTime <= endTime),
            date,
            false
          )
        }}
        style={{
          top, 
          height: visibleHeight, 
          paddingTop: `${verticalPadding}px`,
          paddingBottom: `${verticalPadding}px`,
          fontSize: `${fontSize}px`,
          lineHeight: `${fontSize}px`,
          backgroundColor: getBackgroundColor(type)
        }}>
        <span style={{height: visibleHeight - verticalPadding * 2}}>
          {visibleHeight < fontSize + verticalPadding * 2 ? "" : name}
        </span>
      </button>
    )
  }

  // Receives all events and combine the ones that overlap. E.g. if event 1
  // goes from 7:30 to 8:30 and event 2 from 8:00 to 9:00, they will be combined into
  // a single one going from 7:30 to 9:00. Disjoint events will be preserved.
  const combineEvents = events => {
    // Get all pairs of events and sort them by their start timestamp
    const allPairs = pairs(sortBy(events, 'startBlockTimestamp'))

    for ( let i = 0; i < allPairs.length; i++ ) {
      const pair = allPairs[i]; 
      const currentEvent = pair[0];
      const nextEvent = pair[1];

      // Events overlap when one finishes after the other starts
      if ( currentEvent.endBlockTimestamp > nextEvent.startBlockTimestamp ) {
        const numberOfEvents = currentEvent.numberOfEvents + nextEvent.numberOfEvents;

        // Get the new event end time. Remember that events are ordered by start timestamp,
        // so current event is guaranteed to start first, though we cannot make any 
        // assumption if it will finish first
        const endBlockTimestamp = Math.max(currentEvent.endBlockTimestamp, nextEvent.endBlockTimestamp);
        const endTime = currentEvent.endBlockTimestamp === endBlockTimestamp 
          ? currentEvent.endTime 
          : nextEvent.endTime;

        const newEvent = {
          ...currentEvent,
          type: "MULTIPLE",
          name: `+${numberOfEvents}`,
          numberOfEvents,
          startTime: currentEvent.startTime,
          startBlockTimestamp: currentEvent.startBlockTimestamp,
          endTime,
          endBlockTimestamp
        }

        // Create a new array of events without the two that overlap and including the
        // new one, and then start over again
        const newEvents = [
          ...events.filter(({id}) => id !== currentEvent.id && id !== nextEvent.id),
          newEvent,
        ];
        
        return combineEvents(newEvents);
      }
    }
    return sortBy(events, 'startBlockTimestamp');
  }

  const getAllDayEventsForDay = dayIndex => data
    .filter(event => !event.endTime && moment(event.date).day() === dayIndex);

  const getEventsForDay = dayIndex => {
    let events = null;
    events = data
      .filter(event => event.endTime && moment(event.date).day() === dayIndex)
      .map(event => ({
        ...event, 
        startBlockTimestamp: parseInt(moment(`${event.date} ${event.startTime}`, "YYYY-MM-DD HH:mm").format("X")),
        endBlockTimestamp: parseInt(moment(`${event.date} ${event.endTime}`, "YYYY-MM-DD HH:mm").format("X")),
        numberOfEvents: 1,
      }));

    return combineEvents(events);
  }

  // Generate an interable array with 7 values for each day of the week
  const weekArray = new Array(7).fill(0);

  const getAllDayEventItem = (events, index) => {
    if ( events.length === 0 ) return null;
    return (
      <button
        data-allday={index}
        key={events[0].id}
        aria-label={getLabel(
          events[0].date, 
          "00:00", 
          null, 
          events.length > 1 ? `${events.length} events` : events[0].name
        )}
        onClick={() => {
          setFocusElement({
            selector: `[data-allday="${index}"]`,
            fallBackSelector: `[data-weekheader="${index}"]`
          });
          onEventClick(events, events[0].date, true);
        }}
        className={css['event']} 
        style={{
          position: "relative",
          height: "100%",
          paddingTop: `${verticalPadding}px`,
          paddingBottom: `${verticalPadding}px`,
          fontSize: `${fontSize}px`,
          lineHeight: `${fontSize}px`,
          backgroundColor: getAllDayBackgroundColor(events)
        }}>
        <span style={{height: rowSize - verticalPadding * 2}}>
          {events.length > 1 ? `+${events.length}` :  events[0].name}
        </span>
      </button>
    )
  }

  const getHeader = () => {
    const days = [];
    for ( let i = 0; i < 7; i++ ) {
      days.push(moment(selectedDate).startOf("week").add(i, "days"));
    }
    return days.map((date, i) => (
      <button
        key={i}
        data-weekheader={i}
        role="columnheader"
        aria-label={`Click to see all events for ${moment(date).format("dddd, D")}`}
        className={getSelectedClass(i, css['header-column'])}
        onClick={() => {
          setFocusElement({
            selector: `[data-weekheader="${i}"]`,
            fallBackSelector: `[data-weekheader="${i}"]`
          });
          onHeaderClick(
            data.filter(event => moment(event.date).day() === i),
            date.format("YYYY-MM-DD"),
            false
          )}}>
        <span aria-hidden="true">{date.format("dd")}</span>
        <br />
        <span aria-hidden="true">{date.format("D")}</span>
      </button>
    ))
  }

  return (
    <div className={css['container']} data-testid='calendar'>
      <div role="grid" className={css['calendar']}>
        <div 
          role="presentation" 
          className={css['header-container']} 
          style={{marginRight: scrollWidth}}>
          <div role="row" className={css['header-row']}>
            {getHeader()}
          </div>
          <div role="row" className={css['all-day-row']}>
            <div className={css['all-time-column']}>
              <div className={css['time']}><span>ALL </span>DAY</div>
            </div>
            {weekArray.map((_, i) => (
              <div
                key={i}
                role="gridcell"
                style={{ height: rowSize }}
                className={getSelectedClass(i, css['all-day-column'])}>
                {getAllDayEventItem(getAllDayEventsForDay(i), i)}
              </div>
            ))}
          </div>
        </div>
        <div role="presentation" className={css['events-row-container']}>
          <div className={css['time-column']}>
            {new Array(numberOfHours).fill(0).map((_, i) => (
              <div
                data-testid={`hour-block-${i}`} 
                key={i} 
                className={css['time']} 
                style={{marginTop: rowSize - 12}}>
                {startHour + i > 12 ? startHour + i - 12 : startHour + i} {startHour + i < 12 ? "am" : "pm"}
              </div>
            ))}
          </div>
          <div role="row" className={css['events-row']}>
            {getLines()}
            {weekArray.map((_, i) => (
              <div 
                key={i} 
                role="gridcell"
                style={{ height: rowSize * numberOfHours }} 
                className={getSelectedClass(i, css['events-column'])}>
                {getEventsForDay(i).map(getEventItem)}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

Calendar.defaultProps = {
  startHour: 6,
  endHour: 23,
  
  // Props for styling each event slot. Change it carefully!
  rowSize: 48,
  verticalPadding: 5, 
  fontSize: 12,
};

Calendar.propTypes = {
  startHour: PropTypes.number.isRequired,
  endHour: PropTypes.number.isRequired,
  rowSize: PropTypes.number.isRequired,
  verticalPadding: PropTypes.number,
  fontSize: PropTypes.number,
  data: PropTypes.array.isRequired,
  onEventClick: PropTypes.func.isRequired,
  onHeaderClick: PropTypes.func.isRequired,
  selectedDate: PropTypes.string.isRequired,
  focusID: PropTypes.number
}

export default Calendar;
